// Theme palette is a single source — design tokens.
// See src/styles/tokens.ts (JS) / src/styles/tokens.scss (SCSS). Both must
// match.
import { tokens as THEME_COLORS } from './tokens';

export interface ClockTheme {
  id: string;
  name: string;
  description: string;
  design: {
    fontFamily: string;
    numberType: 'all' | 'cardinal' | 'none';
    handStyle: 'fun' | 'sleek' | 'minimal';
    tickType: 'none' | 'dots' | 'lines';
    background: 'solid' | 'gradient' | 'transparent';
    borderStyle: 'thick' | 'thin' | 'none';
    wedgeStyle: 'full' | 'rim';
  };
  colors: {
    background: string;
    border: string;
    numbers: string | string[];
    hands: {
      hour: string;
      minute: string;
      second: string;
      centerDot: string;
    };
    ticks?: string;
    // App-wide colors
    appBackground: string;
    textColor: string;
    cardBackground: string;      // Background for cards/boxes
    cardBorder: string;          // Border for cards
    // Banner colors
    accentColor: string;
    secondaryText: string;
    logoColor: string;
    dateColor: string;
    tabActive: string;
    tabActiveText: string;
    tabInactive: string;
    tabInactiveBackground: string;
    profileRing: string;
  };
}

export const CLOCK_THEMES: Record<string, ClockTheme> = {
  'kids_light': {
    id: 'kids_light',
    name: 'Kids Light',
    description: 'Warm, friendly, and cheerful. Perfect for day time routines.',
    design: {
      fontFamily: '"Nunito", "Poppins", sans-serif',
      numberType: 'all',
      handStyle: 'fun',
      tickType: 'none',
      background: 'transparent', 
      borderStyle: 'thick',
      wedgeStyle: 'full'
    },
    colors: {
      background: 'rgba(255, 255, 255, 0.8)',
      border: THEME_COLORS.sunYellow,
      // Playful Focus palette for numbers
      numbers: [
        THEME_COLORS.softRed, THEME_COLORS.sunYellow, THEME_COLORS.lavenderPurple, 
        THEME_COLORS.teal, THEME_COLORS.skyBlue, THEME_COLORS.softRed, 
        THEME_COLORS.sunYellow, THEME_COLORS.lavenderPurple, THEME_COLORS.teal, 
        THEME_COLORS.skyBlue, THEME_COLORS.softRed, THEME_COLORS.sunYellow
      ],
      hands: {
        hour: THEME_COLORS.inkBlack,
        minute: THEME_COLORS.lavenderPurple,
        second: THEME_COLORS.softRed,
        centerDot: THEME_COLORS.sunYellow
      },
      // Paper cream background
      appBackground: THEME_COLORS.paperCream,
      textColor: THEME_COLORS.inkBlack,
      cardBackground: 'rgba(255, 255, 255, 0.7)',
      cardBorder: 'rgba(28, 28, 28, 0.08)',
      // Banner colors - warm, playful
      accentColor: THEME_COLORS.teal,
      secondaryText: THEME_COLORS.charcoal,
      logoColor: THEME_COLORS.magenta,
      dateColor: THEME_COLORS.magenta,
      tabActive: THEME_COLORS.teal,
      tabActiveText: THEME_COLORS.white,
      tabInactive: THEME_COLORS.purpleBlue,
      tabInactiveBackground: 'rgba(255, 255, 255, 0.6)',
      profileRing: THEME_COLORS.sunYellow
    }
  },
  'kids_dark': {
    id: 'kids_dark',
    name: 'Kids Dark',
    description: 'Cool, calm, and colorful. Great for evening routines.',
    design: {
      fontFamily: '"Nunito", "Poppins", sans-serif',
      numberType: 'all',
      handStyle: 'fun',
      tickType: 'none',
      background: 'transparent', 
      borderStyle: 'thick',
      wedgeStyle: 'full'
    },
    colors: {
      background: 'rgba(255, 255, 255, 0.1)', 
      border: 'rgba(255, 255, 255, 0.4)',
      // Playful Focus palette for numbers (brighter for dark mode)
      numbers: [
        THEME_COLORS.softRed, THEME_COLORS.sunYellow, THEME_COLORS.lavenderPurple, 
        THEME_COLORS.teal, THEME_COLORS.skyBlue, THEME_COLORS.softRed, 
        THEME_COLORS.sunYellow, THEME_COLORS.lavenderPurple, THEME_COLORS.teal, 
        THEME_COLORS.skyBlue, THEME_COLORS.softRed, THEME_COLORS.sunYellow
      ],
      hands: {
        hour: THEME_COLORS.white, 
        minute: THEME_COLORS.white,
        second: THEME_COLORS.softRed,
        centerDot: THEME_COLORS.sunYellow
      },
      appBackground: THEME_COLORS.charcoal,
      textColor: THEME_COLORS.white,
      cardBackground: 'rgba(255, 255, 255, 0.08)',
      cardBorder: 'rgba(255, 255, 255, 0.15)',
      // Banner colors - playful focus on dark
      accentColor: THEME_COLORS.softRed,
      secondaryText: 'rgba(255, 255, 255, 0.7)',
      logoColor: THEME_COLORS.softRed,
      dateColor: THEME_COLORS.lavenderPurple,
      tabActive: THEME_COLORS.lavenderPurple,
      tabActiveText: THEME_COLORS.white,
      tabInactive: 'rgba(255, 255, 255, 0.7)',
      tabInactiveBackground: 'rgba(255, 255, 255, 0.1)',
      profileRing: THEME_COLORS.sunYellow
    }
  },
  'modern': {
    id: 'modern',
    name: 'Modern',
    description: 'Clean lines, elegant fonts, and a sophisticated look.',
    design: {
      fontFamily: '"Nunito", "Poppins", sans-serif',
      numberType: 'none',
      handStyle: 'sleek',
      tickType: 'lines',
      background: 'solid',
      borderStyle: 'thin',
      wedgeStyle: 'rim'
    },
    colors: {
      // Warm paper cream with subtle sophistication
      background: THEME_COLORS.paperCream,
      border: 'rgba(28, 28, 28, 0.15)',
      numbers: THEME_COLORS.inkBlack,
      ticks: 'rgba(28, 28, 28, 0.3)',
      hands: {
        hour: THEME_COLORS.inkBlack,
        minute: THEME_COLORS.charcoal,
        second: THEME_COLORS.teal,
        centerDot: THEME_COLORS.inkBlack
      },
      // Paper cream background - consistent with theme
      appBackground: THEME_COLORS.paperCream,
      textColor: THEME_COLORS.inkBlack,
      cardBackground: THEME_COLORS.cream,
      cardBorder: 'rgba(28, 28, 28, 0.08)',
      // Banner colors - sophisticated teal accent
      accentColor: THEME_COLORS.teal,
      secondaryText: THEME_COLORS.charcoal,
      logoColor: THEME_COLORS.teal,
      dateColor: THEME_COLORS.teal,
      tabActive: THEME_COLORS.teal,
      tabActiveText: THEME_COLORS.white,
      tabInactive: THEME_COLORS.charcoal,
      tabInactiveBackground: THEME_COLORS.cream,
      profileRing: THEME_COLORS.teal
    }
  },
  'focus': {
    id: 'focus',
    name: 'Focus',
    description: 'High contrast, minimal distractions. Only essential time markers.',
    design: {
      fontFamily: '"Nunito", "Poppins", sans-serif',
      numberType: 'cardinal',
      handStyle: 'minimal',
      tickType: 'dots',
      background: 'solid',
      borderStyle: 'none',
      wedgeStyle: 'full'
    },
    colors: {
      background: THEME_COLORS.paperCream,
      border: THEME_COLORS.inkBlack,
      numbers: THEME_COLORS.inkBlack,
      ticks: THEME_COLORS.charcoal,
      hands: {
        hour: THEME_COLORS.inkBlack,
        minute: THEME_COLORS.inkBlack,
        second: THEME_COLORS.softRed,
        centerDot: THEME_COLORS.inkBlack
      },
      appBackground: THEME_COLORS.paperCream,
      textColor: THEME_COLORS.inkBlack,
      cardBackground: THEME_COLORS.cream,
      cardBorder: 'rgba(28, 28, 28, 0.1)',
      // Banner colors - high contrast with playful focus accent
      accentColor: THEME_COLORS.softRed,
      secondaryText: THEME_COLORS.charcoal,
      logoColor: THEME_COLORS.inkBlack,
      dateColor: THEME_COLORS.softRed,
      tabActive: THEME_COLORS.inkBlack,
      tabActiveText: THEME_COLORS.white,
      tabInactive: THEME_COLORS.charcoal,
      tabInactiveBackground: THEME_COLORS.cream,
      profileRing: THEME_COLORS.softRed
    }
  },
  'playful_focus': {
    id: 'playful_focus',
    name: 'Playful Focus',
    description: 'Calm, minimal, and child-friendly. The signature MyMinu look.',
    design: {
      fontFamily: '"Nunito", "Poppins", sans-serif',
      numberType: 'all',
      handStyle: 'fun',
      tickType: 'dots',
      background: 'solid',
      borderStyle: 'none',
      wedgeStyle: 'full'
    },
    colors: {
      background: THEME_COLORS.paperCream,
      border: THEME_COLORS.inkBlack,
      numbers: THEME_COLORS.inkBlack,
      ticks: THEME_COLORS.inkBlack,
      hands: {
        hour: THEME_COLORS.inkBlack,
        minute: THEME_COLORS.lavenderPurple,
        second: THEME_COLORS.softRed,
        centerDot: THEME_COLORS.sunYellow
      },
      // Paper cream background - signature MyMinu
      appBackground: THEME_COLORS.paperCream,
      textColor: THEME_COLORS.inkBlack,
      cardBackground: 'rgba(255, 255, 255, 0.7)',
      cardBorder: 'rgba(28, 28, 28, 0.08)',
      // Banner colors - signature MyMinu (teal, magenta, yellow)
      accentColor: THEME_COLORS.teal,
      secondaryText: THEME_COLORS.charcoal,
      logoColor: THEME_COLORS.magenta,
      dateColor: THEME_COLORS.magenta,
      tabActive: THEME_COLORS.teal,
      tabActiveText: THEME_COLORS.white,
      tabInactive: THEME_COLORS.purpleBlue,
      tabInactiveBackground: 'rgba(255, 255, 255, 0.6)',
      profileRing: THEME_COLORS.sunYellow
    }
  }
};
